import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FasletWidget } from '@faslet/react-widget';
import { FasletButton } from '@yme/pdp-components/Faslet/components/FasletButton';
import { FASLET_ACTION_TYPE } from '@yme/pdp-components/Faslet/components/constants';
import { useEnhancedAdobeAnalytics, useProductVariantSelector, } from '@yme/pdp-components/Faslet/hooks';
import { setFasletAnalyticTrigger, setItemAddedToCart, } from '@yme/pdp-components/Faslet/redux/actions';
import { getProductFilterOptions } from '@yme/pdp-components/ProductCard';
import { getBrandSlug } from '@yme/selectors/common';
import { getProductName, getProductSlug } from '@yme/selectors/products';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { FasletWrapperStyled } from './styled';
export function FasletSizeWidget(props) {
    const { currentProduct: { images }, products, onSelectSize, buttonText, alternateButtonText, shopId = '', children, } = props;
    const dispatch = useDispatch();
    const useLabelRef = useRef('');
    const [buttonLabel, setButtonText] = useState(buttonText);
    const [currentSize, setCurrentSize] = useState('');
    const [isShown, setIsShown] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const [refValue, setRefValue] = useState(useLabelRef.current);
    const brandSlug = useSelector(getBrandSlug) || '';
    const productSlug = useSelector(getProductSlug);
    const productName = useSelector(getProductName);
    const filteredSku = useSelector(getProductFilterOptions)?.sku;
    const updateRef = (newValue) => {
        useLabelRef.current = newValue;
        setRefValue(newValue);
    };
    const { selectSizeByDomResult, getSizeBySku, getSkuBySize } = useProductVariantSelector({
        products,
        onSelectSize,
        shopId,
    });
    const { triggerAnalytics } = useEnhancedAdobeAnalytics(setDigitalData);
    useEffect(() => {
        if (filteredSku && refValue && !isOpened) {
            triggerAnalytics(DigitalDataMutation.FASLET_RECOMMENDED_SIZE, filteredSku);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refValue]);
    const handleClose = useCallback(() => {
        if (!currentSize) {
            selectSizeByDomResult(currentSize);
        }
    }, [currentSize, selectSizeByDomResult]);
    const handleOpen = useCallback(() => {
        const sku = getSkuBySize(refValue) || filteredSku;
        if (sku) {
            triggerAnalytics(DigitalDataMutation.FASLET_LOADED, sku);
        }
        setIsOpened(true);
    }, [filteredSku, getSkuBySize, refValue, triggerAnalytics]);
    const selectSize = useCallback((size) => products?.find((product) => product.size === size)?.size, [products]);
    const selectPrevSize = useCallback((variant) => getSizeBySku(variant?.id), [getSizeBySku]);
    const handleResult = useCallback(({ label, variant }, _, action) => {
        const size = selectSize(label);
        const variantSize = selectPrevSize(variant);
        const isEqualSize = size === variantSize;
        if (size) {
            onSelectSize?.(size);
        }
        const shouldSetResult = ![FASLET_ACTION_TYPE.afterClose, FASLET_ACTION_TYPE.preCalculated].includes(action) &&
            !isEqualSize;
        if (shouldSetResult && size) {
            const sku = getSkuBySize(size);
            triggerAnalytics(DigitalDataMutation.FASLET_RECOMMENDED_SIZE, sku);
        }
        if (label) {
            setButtonText(`${alternateButtonText} ${label}`);
            if (isEqualSize) {
                updateRef(label);
            }
        }
    }, [alternateButtonText, getSkuBySize, onSelectSize, selectPrevSize, selectSize, triggerAnalytics]);
    const addToCart = useCallback(async (sku) => {
        const size = getSizeBySku(sku);
        if (size) {
            onSelectSize?.(size);
            dispatch(setItemAddedToCart(true));
            dispatch(setFasletAnalyticTrigger(true));
            setCurrentSize(size);
            triggerAnalytics(DigitalDataMutation.FASLET_ADDED_TO_CART, sku);
        }
    }, [dispatch, getSizeBySku, onSelectSize, triggerAnalytics]);
    const productColors = useMemo(() => {
        return products?.map(({ colourId, colourName }) => ({
            id: colourId,
            name: colourName || colourId,
        }));
    }, [products]);
    const productVariants = useMemo(() => {
        return products?.length
            ? products?.map(({ sku, size, colourId, prices, images, stock }) => {
                return {
                    variantId: sku,
                    sizeLabel: size,
                    sku,
                    colorId: colourId,
                    price: prices.amount,
                    imageUrl: images[0]?.url,
                    inStock: stock > 0,
                };
            })
            : [];
    }, [products]);
    const handleShowButton = () => {
        setIsShown(true);
    };
    const handleClick = useCallback(() => {
        window?._faslet?.openWidget?.();
    }, []);
    if (!productVariants.length) {
        return null;
    }
    return (React.createElement(FasletWrapperStyled, null,
        React.createElement(FasletButton, { shopId: shopId, buttonLabel: buttonLabel, isShown: isShown, ...(isShown && {
                handleClick,
            }) }, children),
        React.createElement(FasletWidget, { productId: productSlug, shopId: shopId, brandId: brandSlug, productImageUrl: images[0]?.url, productName: productName, colors: productColors, variants: productVariants, onAddToCart: addToCart, 
            // TODO: will remove when Faslet change API, we have 3 params instead of 2
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onResult: handleResult, onClose: handleClose, onButtonShow: handleShowButton, onOpen: handleOpen })));
}
